import { useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
  useVelocity,
} from "framer-motion";
import { Check, Copy, ExternalLink, Smartphone, X } from "lucide-react";

import PhoneNumberField from "@/components/linking/PhoneNumberField";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { DESKTOP_QR_DISPLAY_SIZES, MOBILE_QR_DISPLAY_SIZES } from "@/lib/living-qr-display";
import { useQrCursorLag } from "@/hooks/use-qr-cursor-lag";
import { INSTANT_QR_PROTOTYPE_POLL_DELAY_MS } from "@/lib/instant-qr-prototype";
import { getLinkingPhoneState } from "@/lib/linking-phone";
import "@/components/landing/instant-qr-prototype.css";

const PROTOTYPE_CODE = "ABCD-EFGH";

function useQrMotionDisabled() {
  const [disabled, setDisabled] = useState(() =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches || navigator.connection?.saveData === true);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const connection = navigator.connection;
    const sync = () => setDisabled(media.matches || connection?.saveData === true);
    media.addEventListener("change", sync);
    connection?.addEventListener?.("change", sync);
    sync();
    return () => {
      media.removeEventListener("change", sync);
      connection?.removeEventListener?.("change", sync);
    };
  }, []);

  return disabled;
}

function copyTextWithLegacySelection(value) {
  const textArea = document.createElement("textarea");
  textArea.value = value;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.inset = "-100vh auto auto -100vw";
  textArea.style.opacity = "0";
  document.body.append(textArea);
  textArea.focus();
  textArea.select();
  const copied = document.execCommand?.("copy") === true;
  textArea.remove();
  return copied;
}

async function copyPairingCode(value = PROTOTYPE_CODE) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Private HTTP hosts can deny Clipboard API access; keep the local fallback.
  }

  try {
    return copyTextWithLegacySelection(value);
  } catch {
    return false;
  }
}

function CircularScanLabel() {
  const { tx } = useI18n();

  return (
    <div className="living-qr__orbit" aria-hidden="true">
      <span>{tx("Scan to link Min")}</span>
      <span>{tx("No email needed.")}</span>
      <span>{tx("Scan to link Min")}</span>
      <span>{tx("No email needed.")}</span>
    </div>
  );
}

function QrSquare({
  imageSrc = "/landing/instant-qr-demo.png",
  motionDisabled,
  polling,
  onActivate,
  onResize,
  sizeIndex = 0,
  testId = "living-qr-scan",
}) {
  const { tx } = useI18n();

  return (
    <button
      type="button"
      className={`living-qr__square living-qr__square--qr${polling ? " is-polling" : ""}${motionDisabled ? " is-motion-disabled" : ""}`}
      aria-label={onResize ? tx(sizeIndex === 2 ? "Reset QR code size" : "Enlarge QR code") : tx("Scan to link Min")}
      onClick={onResize || onActivate}
      data-testid={testId}
    >
      <img src={imageSrc} alt="" width="640" height="640" draggable="false" />
      <span className="sr-only" role="status" aria-live="polite">
        {polling ? tx("Waiting for WhatsApp") : ""}
      </span>
    </button>
  );
}

function RecoveringSquare({ testId = "living-qr-recovering" }) {
  const { tx } = useI18n();
  return (
    <div
      className="living-qr__square living-qr__square--recovering"
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      <span>{tx("Getting a fresh QR…")}</span>
    </div>
  );
}

function LinkedSquare({
  configureTestId = "living-qr-configure",
  onConfigure,
  testId = "living-qr-linked",
}) {
  const { tx } = useI18n();

  return (
    <div className="living-qr__linked" data-testid={testId}>
      <div className="living-qr__square living-qr__square--linked" aria-hidden="true">
        <Check />
      </div>
      <strong>{tx("Min is linked.")}</strong>
      <Button type="button" size="sm" onClick={onConfigure} data-testid={configureTestId}>
        {tx("Configure Min")}
      </Button>
    </div>
  );
}

function ConfiguredSquare({ onOpenMin, testId = "living-qr-configured" }) {
  const { tx } = useI18n();

  return (
    <button
      type="button"
      className="living-qr__configured"
      onClick={onOpenMin}
      data-testid={testId}
    >
      <span className="living-qr__square living-qr__square--configured">
        <img src="/landing/min-mark.png" alt="" width="192" height="192" />
      </span>
      <strong>{tx("My Min")}</strong>
    </button>
  );
}

export default function InstantQrPrototypePanel({
  awakeSignal = 0,
  designModeEnabled = false,
  onActivate,
  onConfigure,
  onOpenMin,
  onUsePhone,
  polling = false,
  qrImageSrc = "/landing/instant-qr-demo.png",
  qrReady = false,
  recovering = false,
  state,
}) {
  const { tx } = useI18n();
  const stageRef = useRef(null);
  const homeRef = useRef(null);
  const scanButtonRef = useRef(null);
  const draggedRef = useRef(false);
  const awakeTimeoutRef = useRef(null);
  const [dismissed, setDismissed] = useState(false);
  const [awake, setAwake] = useState(false);
  const [sizeIndex, setSizeIndex] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [keyboardFocus, setKeyboardFocus] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 59.999rem)").matches);
  const motionDisabled = useQrMotionDisabled();
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const lagTarget = useTransform(scrollVelocity, [-1600, 0, 1600], [18, 0, -18]);
  const lagY = useSpring(lagTarget, { damping: 20, stiffness: 150, mass: 0.55 });
  const dragX = useMotionValue(0);
  const dragY = useMotionValue(0);
  const visibleState = state === "configured"
    ? "configured"
    : state === "linked"
      ? "linked"
      : recovering && !qrReady
        ? "recovering"
        : "qr";
  const phoneSelected = state === "phone" || state === "code";
  const scanLocked = hovered || keyboardFocus || pinned || sizeIndex > 0 || visibleState !== "qr";
  const qrSizes = mobile ? MOBILE_QR_DISPLAY_SIZES : DESKTOP_QR_DISPLAY_SIZES;
  const qrSize = qrSizes[visibleState === "qr" ? sizeIndex : 0];
  const cursor = useQrCursorLag({
    stageRef, homeRef, dragX, dragY, size: qrSize,
    disabled: motionDisabled || mobile || dismissed || visibleState !== "qr",
    paused: scanLocked,
  });

  useEffect(() => () => window.clearTimeout(awakeTimeoutRef.current), []);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 59.999rem)");
    const syncMobile = () => setMobile(query.matches);
    query.addEventListener?.("change", syncMobile);
    return () => query.removeEventListener?.("change", syncMobile);
  }, []);

  useEffect(() => {
    const resetDragInsideViewport = () => {
      dragX.set(0);
      dragY.set(0);
      setPinned(false);
    };
    window.addEventListener("resize", resetDragInsideViewport);
    return () => window.removeEventListener("resize", resetDragInsideViewport);
  }, [dragX, dragY]);

  useEffect(() => {
    if (!awakeSignal || dismissed || !qrReady) return;
    setAwake(true);
    scanButtonRef.current = document.querySelector('[data-testid="living-qr-scan"]');
    scanButtonRef.current?.focus({ preventScroll: true });
    window.clearTimeout(awakeTimeoutRef.current);
    awakeTimeoutRef.current = window.setTimeout(() => setAwake(false), 1800);
  }, [awakeSignal, dismissed, qrReady]);

  if (dismissed || phoneSelected || (visibleState === "qr" && !qrReady)) return null;

  function activateQr() {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    setSizeIndex((current) => (current + 1) % qrSizes.length);
  }

  return (
    <div ref={stageRef} className="living-qr__desktop-stage" data-testid="living-qr-desktop"
      data-qr-size={["small", "medium", "large"][sizeIndex]}
      style={{ "--living-qr-size": `min(${qrSize}px, calc(100vh - 10rem))` }}>
      <motion.div
        ref={homeRef}
        className="living-qr__scroll-lag"
        style={{ y: motionDisabled || scanLocked ? 0 : lagY }}
      >
        <motion.div className="living-qr__cursor-lag" style={{ x: cursor.x, y: cursor.y }}>
        <motion.div
          className={`living-qr__drag-shell${awake ? " is-awake" : ""}${scanLocked ? " is-scan-locked" : ""}`}
          style={{ x: dragX, y: dragY }}
          drag
          dragConstraints={stageRef}
          dragElastic={0}
          dragMomentum={false}
          onPointerDown={() => { draggedRef.current = false; }}
          onPointerEnter={() => { cursor.freeze(); setHovered(true); }}
          onPointerLeave={() => setHovered(false)}
          onFocusCapture={(event) => setKeyboardFocus(event.target.matches(":focus-visible"))}
          onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setKeyboardFocus(false); }}
          onDragStart={() => { draggedRef.current = true; cursor.freeze(); setPinned(true); }}
          whileDrag={motionDisabled ? undefined : { scale: 1.03 }}
        >
          <div className="living-qr__meander">
            <div className="living-qr__halo" aria-hidden="true" />
            {visibleState === "qr" ? <CircularScanLabel /> : null}
            <button
              type="button"
              className="living-qr__dismiss"
              aria-label={tx("Close")}
              onClick={() => setDismissed(true)}
              data-testid="living-qr-dismiss"
            >
              <X aria-hidden="true" />
            </button>
            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={visibleState}
                className="living-qr__face"
                initial={motionDisabled ? false : { opacity: 0, scale: 0.72, rotate: -8 }}
                animate={{ opacity: 1, scale: 1, rotate: 0 }}
                exit={motionDisabled ? undefined : { opacity: 0, scale: 0.72, rotate: 8 }}
                transition={{ duration: motionDisabled ? 0.01 : 0.34, ease: [0.2, 0.8, 0.2, 1] }}
              >
                {visibleState === "qr" ? (
                  <QrSquare
                    imageSrc={qrImageSrc}
                    motionDisabled={motionDisabled}
                    polling={polling}
                    onResize={activateQr}
                    sizeIndex={sizeIndex}
                  />
                ) : null}
                {visibleState === "recovering" ? <RecoveringSquare /> : null}
                {visibleState === "linked" ? <LinkedSquare onConfigure={onConfigure} /> : null}
                {visibleState === "configured" ? <ConfiguredSquare onOpenMin={onOpenMin} /> : null}
              </motion.div>
            </AnimatePresence>
            {mobile && visibleState === "qr" ? (
              <button type="button" className="living-qr__use-phone" onClick={() => onUsePhone?.({ preserveTransitionScroll: false })} data-testid="living-qr-use-phone">
                {tx("Use phone number instead")}
              </button>
            ) : null}
            {designModeEnabled && visibleState === "qr" ? (
              <button type="button" className="living-qr__simulate" onClick={onActivate} data-testid="living-qr-simulate-scan">
                {tx("Simulate scan")}
              </button>
            ) : null}
          </div>
        </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export function ProcessInstantLinkPrototype({
  awakeSignal = 0,
  onActivate,
  onConfigure,
  onOpenMin,
  onOpenWhatsApp,
  onRequestCode,
  onStateChange,
  pairingCode = PROTOTYPE_CODE,
  polling = false,
  qrImageSrc = "/landing/instant-qr-demo.png",
  qrReady = false,
  recovering = false,
  state,
}) {
  const { tx } = useI18n();
  const rootRef = useRef(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [copyStatus, setCopyStatus] = useState("idle");
  const [codePending, setCodePending] = useState(false);
  const [handoffPending, setHandoffPending] = useState(false);
  const timeoutRef = useRef(null);
  const submitScrollPositionRef = useRef(null);
  const lastAwakeSignalRef = useRef(awakeSignal);
  const motionDisabled = useQrMotionDisabled();
  const mobileState = ["code", "linked", "configured"].includes(state) ? state : "phone";
  const desktopState = state === "configured" ? "configured" : state === "linked" ? "linked" : "qr";
  const mobileStateRef = useRef(mobileState);
  mobileStateRef.current = mobileState;

  useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

  useEffect(() => {
    if (mobileState === "code") return;
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setHandoffPending(false);
  }, [mobileState]);

  useEffect(() => {
    if (awakeSignal === lastAwakeSignalRef.current) return;
    lastAwakeSignalRef.current = awakeSignal;
    if (!awakeSignal || !window.matchMedia("(max-width: 59.999rem)").matches) return;
    rootRef.current?.scrollIntoView({
      behavior: motionDisabled ? "auto" : "smooth",
      block: "center",
    });
    const focusFrame = window.requestAnimationFrame(() => {
      document.getElementById("living-qr-phone-number")?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(focusFrame);
  }, [awakeSignal, motionDisabled]);

  useEffect(() => {
    if (state !== "phone") return;
    rootRef.current?.scrollIntoView({
      behavior: motionDisabled ? "auto" : "smooth",
      block: "center",
    });
    const focusFrame = window.requestAnimationFrame(() => {
      document.getElementById("living-qr-phone-number")?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(focusFrame);
  }, [motionDisabled, state]);

  async function requestCode(event) {
    event.preventDefault();
    const phoneState = getLinkingPhoneState(phoneNumber);
    if (!phoneState.canSubmit) {
      submitScrollPositionRef.current = null;
      setPhoneError(true);
      return;
    }
    setPhoneError(false);
    const preservePosition = submitScrollPositionRef.current;
    submitScrollPositionRef.current = null;
    if (onRequestCode) {
      setCodePending(true);
      const ready = await onRequestCode(phoneState.normalizedPhoneNumber);
      setCodePending(false);
      if (!ready) return;
    }
    onStateChange("code", { preservePosition });
  }

  function captureSubmitScrollPosition() {
    submitScrollPositionRef.current = { left: window.scrollX, top: window.scrollY };
  }

  async function handleCopy() {
    const copied = await copyPairingCode(pairingCode);
    setCopyStatus(copied ? "copied" : "failed");
  }

  function simulateWhatsAppHandoff() {
    if (onOpenWhatsApp) {
      onOpenWhatsApp();
      return;
    }
    if (handoffPending) return;
    setHandoffPending(true);
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      setHandoffPending(false);
      if (mobileStateRef.current !== "code") return;
      onStateChange("linked");
    }, INSTANT_QR_PROTOTYPE_POLL_DELAY_MS);
  }

  return (
    <div ref={rootRef} className="living-qr__process" data-testid="living-qr-process">
      <div className="living-qr__process-desktop" data-testid="living-qr-process-desktop">
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={desktopState}
            className="living-qr__process-face"
            initial={motionDisabled ? false : { opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={motionDisabled ? undefined : { opacity: 0, scale: 0.92 }}
            transition={{ duration: motionDisabled ? 0.01 : 0.24 }}
          >
            {desktopState === "qr" && qrReady ? (
              <div className="living-qr__process-qr">
                <strong>{tx("Scan to link Min")}</strong>
                <QrSquare
                  imageSrc={qrImageSrc}
                  motionDisabled={motionDisabled}
                  polling={polling}
                  onActivate={onActivate}
                  testId="living-qr-process-scan"
                />
                <span>{tx("No email needed.")}</span>
              </div>
            ) : null}
            {desktopState === "qr" && recovering && !qrReady ? (
              <RecoveringSquare testId="living-qr-process-recovering" />
            ) : null}
            {desktopState === "linked" ? (
              <LinkedSquare
                configureTestId="living-qr-process-configure"
                onConfigure={onConfigure}
                testId="living-qr-process-linked"
              />
            ) : null}
            {desktopState === "configured" ? (
              <ConfiguredSquare onOpenMin={onOpenMin} testId="living-qr-process-configured" />
            ) : null}
          </motion.div>
        </AnimatePresence>
      </div>

      <section
        className="living-qr__process-mobile"
        data-testid="living-qr-process-mobile"
        data-prototype-state={mobileState}
      >
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={mobileState}
            className="living-qr__mobile-face"
            initial={motionDisabled ? false : { opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={motionDisabled ? undefined : { opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: motionDisabled ? 0.01 : 0.28 }}
          >
          {mobileState === "phone" ? (
            <form className="living-qr__mobile-card" onSubmit={requestCode} data-testid="living-qr-phone">
              <Smartphone className="living-qr__process-phone-icon" aria-hidden="true" />
              {state === "phone" ? (
                <button type="button" className="living-qr__mobile-back" onClick={() => onStateChange("qr")}>
                  {tx("Back")}
                </button>
              ) : null}
              <label htmlFor="living-qr-phone-number">{tx("WhatsApp number")}</label>
              <PhoneNumberField
                id="living-qr-phone-number"
                defaultCountry="za"
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
              {phoneError ? <p role="alert">{tx("Enter your WhatsApp number.")}</p> : null}
              <Button type="submit" disabled={codePending} onPointerDown={captureSubmitScrollPosition} data-testid="living-qr-get-code">
                {tx(codePending ? "Getting your code" : "Get code")}
              </Button>
            </form>
          ) : null}

          {mobileState === "code" ? (
            <div className="living-qr__mobile-card" data-testid="living-qr-code">
              <button type="button" className="living-qr__mobile-back" onClick={() => onStateChange("phone")}>
                {tx("Back")}
              </button>
              <span>{tx("Enter this inside WhatsApp")}</span>
              <strong className="living-qr__prototype-code" aria-label={pairingCode}>{pairingCode}</strong>
              <div className="living-qr__mobile-actions">
                <Button type="button" variant="outline" className="living-qr__copy-code" onClick={() => void handleCopy()}>
                  <Copy aria-hidden="true" />
                  {tx("Copy code")}
                </Button>
                <Button type="button" onClick={simulateWhatsAppHandoff} disabled={handoffPending} data-testid="living-qr-open-whatsapp">
                  <ExternalLink aria-hidden="true" />
                  {tx(handoffPending ? "Waiting for WhatsApp" : "Open WhatsApp")}
                </Button>
              </div>
              <p role="status" aria-live="polite">
                {copyStatus === "copied"
                  ? tx("Copied. If WhatsApp won’t paste it, keep this page open and type the code.")
                  : null}
                {copyStatus === "failed" ? tx("Copy failed. Try again.") : null}
              </p>
            </div>
          ) : null}

          {mobileState === "linked" ? (
            <div className="living-qr__mobile-success">
              <div className="living-qr__square living-qr__square--linked"><Check aria-hidden="true" /></div>
              <strong>{tx("Min is linked.")}</strong>
              <Button type="button" onClick={onConfigure}>{tx("Configure Min")}</Button>
            </div>
          ) : null}

          {mobileState === "configured" ? <ConfiguredSquare onOpenMin={onOpenMin} /> : null}
          </motion.div>
        </AnimatePresence>
      </section>
    </div>
  );
}
